import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { addDays, isToday, setMinutes, setSeconds, setMilliseconds, addHours } from '@e3dc-react/shell/Libs/date'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'

import { DatePicker } from 'src/Libs/inputs/react-hook-form'
import { ScheduleDescription, ScheduleName, ScheduleRepeatLabel, ScheduleSlider, ScheduleStartTime } from './form'
import {
  ChargingScheduleFormFieldNames,
  ChargingScheduleFormType,
  RepeatOptions,
  RepeatOptionsType,
  ScheduleCounter,
} from 'src/Libs/e-mobility/vehicle-integration'
import { getScheduleNamePlaceholder } from '../../functions'
import { DAYS_OF_WEEK, DAYS_OF_WEEK_PLUS_ONE } from '../../consts'
import DeleteChargingScheduleModal from './ConfirmScheduleDeleteModal'

interface Props {
  chargeLimitMax?: number
  isEditing: boolean
  minStateOfCharge: number
  scheduleCounter: ScheduleCounter
  temporaryScheduleName?: string
  handleOnDelete: () => Promise<void>
}

/**
 *  Component to set charging schedule
 */
const ChargingScheduleForm: React.FC<Props> = ({
  handleOnDelete,
  scheduleCounter,
  temporaryScheduleName,
  minStateOfCharge,
  chargeLimitMax,
  isEditing,
}) => {
  const { t } = useCustomTranslation()
  const { control, setValue } = useFormContext<ChargingScheduleFormType>()
  const repeat = useWatch({ control, name: ChargingScheduleFormFieldNames.Repeat })
  const startDate = useWatch({ control, name: ChargingScheduleFormFieldNames.StartDate })
  const endDate = useWatch({ control, name: ChargingScheduleFormFieldNames.EndDate })
  const [counter, setCounter] = useState(0)
  const [isDeleteChargingScheduleModalOpen, setIsDeleteChargingScheduleModalOpen] = useState(false)

  const handleChangeScheduleType = (repeatOptions: RepeatOptionsType): void => {
    setValue(ChargingScheduleFormFieldNames.Repeat, repeatOptions)
    if (repeatOptions === RepeatOptions.doNotRepeat) {
      setValue(ChargingScheduleFormFieldNames.EndDate, null)
      setValue(ChargingScheduleFormFieldNames.SelectedWeekDays, { mo: false, tu: false, we: false, th: false, fr: false, sa: false, su: false })
    } else {
      setValue(ChargingScheduleFormFieldNames.EndDate, addDays(new Date(), DAYS_OF_WEEK_PLUS_ONE))
      setValue(ChargingScheduleFormFieldNames.SelectedWeekDays, { mo: true, tu: true, we: true, th: true, fr: true, sa: true, su: true })
    }
  }

  const handleDateTimeTransform = useCallback((date: Moment | null): Date | null => {
    if (!date) return null
    return date.toDate()
  }, [])

  useEffect(() => {
    if (endDate && startDate > endDate) {
      const newEndDate = addDays(startDate, DAYS_OF_WEEK)
      setValue(ChargingScheduleFormFieldNames.EndDate, newEndDate)
    }

    // if start date is today, set start time to the next full hour (e.g. 10:15 -> 11:00)
    if (isToday(startDate) && !isEditing) {
      const now = new Date()
      const nextHour = setMinutes(setSeconds(setMilliseconds(addHours(now, 1), 0), 0), 0)
      setValue(ChargingScheduleFormFieldNames.StartTime, nextHour, { shouldValidate: true })
    }
  }, [endDate, isEditing, setValue, startDate])

  useEffect(() => {
    if (repeat === RepeatOptions.doNotRepeat) {
      setCounter(scheduleCounter.oneTime)
    } else if (repeat === RepeatOptions.daily) {
      setCounter(scheduleCounter.daily)
    } else {
      setCounter(scheduleCounter.weekly)
    }
  }, [repeat, scheduleCounter])

  const handleCloseDeleteChargingScheduleModal = (): void => {
    setIsDeleteChargingScheduleModalOpen(false)
  }

  const oneTimeSchedule = (
    <>
      {!isEditing && (
        <>
          <Typography sx={{ textAlign: 'center' }} variant="body2">
            {t('emobility.vehicle-integration.charging-schedule.change-charging-schedule-type')}
          </Typography>
          <Button onClick={() => handleChangeScheduleType(RepeatOptions.daily)} variant="text">
            {t('emobility.vehicle-integration.charging-schedule.change-charging-schedule-to-recurring')}
          </Button>
        </>
      )}
    </>
  )

  const recurringSchedule = (
    <>
      <Stack gap={2}>
        <ScheduleRepeatLabel />
      </Stack>

      <Stack gap={2} direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ flex: 1 }} variant="h3">
          {t('emobility.vehicle-integration.charging-schedule.end-date-label')}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <DatePicker name="endDate" textFieldStyles={{ width: '100%' }} minDate={moment(startDate)} onTransform={handleDateTimeTransform} />
        </Box>
      </Stack>

      <ScheduleDescription />

      {!isEditing && (
        <Button onClick={() => handleChangeScheduleType(RepeatOptions.doNotRepeat)} variant="text">
          {t('emobility.vehicle-integration.charging-schedule.change-charging-schedule-to-one-time')}
        </Button>
      )}
    </>
  )

  return (
    <>
      <Stack gap={4} mt={2}>
        {repeat !== RepeatOptions.doNotRepeat && (
          <ScheduleName
            scheduleCounter={counter}
            scheduleNamePlaceholderKey={t(getScheduleNamePlaceholder(repeat))}
            temporaryScheduleName={temporaryScheduleName}
          />
        )}

        <ScheduleSlider minStateOfCharge={minStateOfCharge} chargeLimitMax={chargeLimitMax} />

        <Stack gap={2} direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ flex: 1 }} variant="h3">
            {repeat === RepeatOptions.doNotRepeat
              ? t('emobility.vehicle-integration.charging-schedule.departure-date-label')
              : t('emobility.vehicle-integration.charging-schedule.start-date-label')}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <DatePicker
              name="startDate"
              textFieldStyles={{ width: '100%' }}
              minDate={isEditing ? undefined : moment()}
              onTransform={handleDateTimeTransform}
            />
          </Box>
        </Stack>

        <ScheduleStartTime isEditing={isEditing} handleDateTimeTransform={handleDateTimeTransform} />

        {repeat === RepeatOptions.doNotRepeat ? oneTimeSchedule : recurringSchedule}
      </Stack>
      {isEditing && (
        <Button sx={{ display: 'flex', ml: 'auto', mr: 'auto', mt: 4 }} onClick={() => setIsDeleteChargingScheduleModalOpen(true)} variant="text">
          {t('emobility.vehicle-integration.charging-schedule.delete-charging-schedule')}
        </Button>
      )}
      {isDeleteChargingScheduleModalOpen && (
        <DeleteChargingScheduleModal
          handleOnDelete={handleOnDelete}
          onClose={handleCloseDeleteChargingScheduleModal}
          isDialogOpen={isDeleteChargingScheduleModalOpen}
        />
      )}
    </>
  )
}

export default ChargingScheduleForm
